import { useState } from "react";
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Select,
  Spinner,
  Switch,
  Textarea,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Save24Regular } from "@fluentui/react-icons";
import { ESITI, SOGGETTO_PRINCIPALE, TIPI_ENTE, VALUTAZIONI, type Esito, type TipoEnte, type TipoScheda, type Valutazione } from "@app/schema";
import { GraphError } from "@app/sp-data";
import { useData } from "../data/DataProvider";
import { useServizi } from "../data/useServizi";
import { MAX_TESTO, creaGara, dataSoloGiorno, eAlbo, giornoItaliano, oggettoGara, salvaGara, type DatiGara, type Gara } from "../data/gare";
import { testoMandanti } from "../data/checklist";
import { useAsync } from "../lib/useAsync";
import { formatNumberInput, fromLocalInput, parseNumber, toLocalInput } from "../lib/format";
import { goBack, replace } from "../lib/nav";
import { Shell } from "../components/Shell";
import { Section } from "../components/Section";
import { ChipGroup } from "../components/ChipGroup";
import { ErrorState, describeError } from "../components/ErrorState";

const useStyles = makeStyles({
  two: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: tokens.spacingHorizontalM,
    alignItems: "start",
    // Senza minWidth 0 un menu con voci lunghe allarga la colonna e copre il campo accanto
    "& > *": { minWidth: 0 },
  },
  full: { width: "100%", minWidth: 0 },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacingHorizontalM },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  cols: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 3fr) minmax(320px, 2fr)",
    gap: tokens.spacingHorizontalL,
    alignItems: "start",
    "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  col: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM, minWidth: 0 },
});

/** Nuova gara o nuovo albo (id assente) oppure modifica di una scheda esistente. */
export function ModificaGara({ id, tipo }: { id?: number; tipo?: TipoScheda }) {
  const data = useData();
  const gara = useAsync(() => (id ? data.gare.get(id).then((x) => x ?? null) : Promise.resolve(null)), [data, id]);
  if (id && (gara.error || gara.data === undefined || gara.data === null))
    return (
      <Shell title="Modifica gara" onBack={() => goBack()}>
        {gara.error ? (
          <ErrorState error={gara.error} />
        ) : gara.data === null ? (
          <ErrorState error={new Error("Gara non trovata.")} />
        ) : (
          <Spinner style={{ marginTop: 48 }} />
        )}
      </Shell>
    );
  if (!id && gara.data === undefined) return <Spinner style={{ marginTop: 48 }} />;
  return <Form key={gara.data?.etag ?? `nuova-${tipo ?? "Gara"}`} g={gara.data ?? undefined} tipo={tipo} onReload={gara.reload} />;
}

function Form({ g, tipo, onReload }: { g?: Gara; tipo?: TipoScheda; onReload: () => void }) {
  const s = useStyles();
  const data = useData();
  const { servizi } = useServizi();
  const f = g?.fields ?? {};
  const albo = g ? eAlbo(g) : tipo === "Albo";
  const [iscrizione, setIscrizione] = useState(f.DataIscrizione ? giornoItaliano(f.DataIscrizione) : "");
  const [rinnovo, setRinnovo] = useState(f.ScadenzaIscrizione ? giornoItaliano(f.ScadenzaIscrizione) : "");
  const [oggetto, setOggetto] = useState(g ? oggettoGara(g) : "");
  const [stazione, setStazione] = useState(f.Stazione ?? "");
  const [servizio, setServizio] = useState(f.Servizio ? String(f.Servizio) : "");
  const [cig, setCig] = useState(f.CIG ?? "");
  const [importo, setImporto] = useState(formatNumberInput(f.Importo));
  const [scadenza, setScadenza] = useState(toLocalInput(f.Scadenza));
  const [interesse, setInteresse] = useState(toLocalInput(f.ScadenzaInteresse));
  const [buste, setBuste] = useState(toLocalInput(f.AperturaBuste));
  const [bando, setBando] = useState(f.LinkBando ?? "");
  const [perLotti, setPerLotti] = useState(!!f.PerLotti);
  const [rti, setRti] = useState(f.Mandanti ?? "");
  const [valutazione, setValutazione] = useState<Valutazione>((f.Valutazione as Valutazione) ?? "Da valutare");
  const [note, setNote] = useState(f.NoteValutazione ?? "");
  const [esito, setEsito] = useState<Esito | "">((f.Esito as Esito) ?? "");
  const [aggiudicato, setAggiudicato] = useState(formatNumberInput(f.ImportoAggiudicato));
  const [tipoEnte, setTipoEnte] = useState<TipoEnte | "">((f.TipoEnte as TipoEnte) ?? "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; conflict?: boolean }>();
  const cartelleCreate = !!f.CartellaId || !!f.PosizioneId;

  // Servizi attivi, più quello già scelto anche se disattivato
  const opzioni = (servizi ?? []).filter((x) => x.fields.Attivo !== false || x.id === f.Servizio);

  async function save() {
    const imp = parseNumber(importo);
    if (!oggetto.trim()) return setMsg({ text: albo ? "Indica l'albo o l'elenco." : "Indica l'oggetto della gara." });
    if (!stazione.trim()) return setMsg({ text: albo ? "Indica l'ente." : "Indica la stazione appaltante." });
    if (albo && iscrizione && rinnovo && rinnovo < iscrizione) return setMsg({ text: "La scadenza dell'iscrizione è precedente alla data di iscrizione." });
    if (!servizio) return setMsg({ text: "Scegli il servizio: la sua sigla entra nel nome della cartella." });
    if (importo.trim() && imp === undefined) return setMsg({ text: "Importo non valido." });
    const agg = parseNumber(aggiudicato);
    if (aggiudicato.trim() && agg === undefined) return setMsg({ text: "Importo aggiudicato non valido." });
    if (bando.trim() && !/^https?:\/\//i.test(bando.trim())) return setMsg({ text: "Il link al bando deve iniziare con http:// o https://" });

    const dati: DatiGara = albo
      ? {
          Tipo: "Albo",
          Title: oggetto.trim(),
          Stazione: stazione.trim(),
          Servizio: Number(servizio),
          LinkBando: bando.trim() || undefined,
          NoteValutazione: note.trim() || undefined,
          TipoEnte: tipoEnte || undefined,
          DataIscrizione: dataSoloGiorno(iscrizione),
          ScadenzaIscrizione: dataSoloGiorno(rinnovo),
        }
      : {
      Title: oggetto.trim(),
      Stazione: stazione.trim(),
      Servizio: Number(servizio),
      CIG: perLotti ? undefined : cig.trim() || undefined,
      Importo: imp,
      Scadenza: fromLocalInput(scadenza),
      ScadenzaInteresse: fromLocalInput(interesse),
      AperturaBuste: fromLocalInput(buste),
      LinkBando: bando.trim() || undefined,
      PerLotti: perLotti,
      Mandanti: testoMandanti(rti.split(/[;\n]/)) || undefined,
      Valutazione: valutazione,
      NoteValutazione: note.trim() || undefined,
      Esito: perLotti ? undefined : esito || undefined,
      ImportoAggiudicato: !perLotti && esito === "Aggiudicata" ? agg : undefined,
      TipoEnte: tipoEnte || undefined,
      };
    setSaving(true);
    setMsg(undefined);
    try {
      if (g) {
        await salvaGara(data, g, dati);
        replace(`/g/${g.id}`);
      } else {
        const nuova = await creaGara(data, dati);
        replace(`/g/${nuova.id}`);
      }
    } catch (e) {
      const conflict = e instanceof GraphError && e.isConflict;
      setMsg({ text: describeError(e).detail, conflict });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Shell
      title={g ? `Modifica ${f.Codice ?? ""}` : albo ? "Nuovo albo" : "Nuova gara"}
      onBack={() => goBack()}
      footer={
        <>
          <Button size="large" onClick={() => goBack()} disabled={saving}>
            Annulla
          </Button>
          <Button appearance="primary" size="large" icon={<Save24Regular />} onClick={save} disabled={saving}>
            {saving ? "Salvataggio…" : g ? "Salva" : albo ? "Crea l'albo" : "Crea la gara"}
          </Button>
        </>
      }
    >
      {msg && (
        <MessageBar intent={msg.conflict ? "warning" : "error"}>
          <MessageBarBody>{msg.text}</MessageBarBody>
          {msg.conflict && (
            <MessageBarActions>
              <Button size="small" onClick={onReload}>
                Ricarica
              </Button>
            </MessageBarActions>
          )}
        </MessageBar>
      )}
      {!g && (
        <MessageBar intent="info">
          <MessageBarBody>Il codice OA si assegna al salvataggio{albo ? ", con il suffisso _ALBO" : ""}.</MessageBarBody>
        </MessageBar>
      )}

      {albo ? (
        <div className={s.cols}>
          <div className={s.col}>
            <Section title="Albo">
              <Field label="Albo o elenco" required hint={oggetto.trim().length > MAX_TESTO ? `${oggetto.trim().length} caratteri: nell'elenco SharePoint il Titolo ne mostra i primi ${MAX_TESTO}, il testo intero resta nella colonna "Oggetto completo".` : "Es. Albo fornitori servizi sociali, Elenco operatori economici"}>
                <Textarea rows={2} resize="vertical" value={oggetto} onChange={(_, d) => setOggetto(d.value)} />
              </Field>
              <div className={s.two}>
                <Field label="Ente" required>
                  <Input value={stazione} maxLength={MAX_TESTO} placeholder="Es. Comune di Alatri" onChange={(_, d) => setStazione(d.value)} />
                </Field>
                <Field label="Tipo di ente" hint={!tipoEnte ? "Se resta vuoto, si ricava dalla posizione della cartella" : undefined}>
                  <Select className={s.full} value={tipoEnte} onChange={(_, d) => setTipoEnte(d.value as TipoEnte | "")}>
                    <option value="">—</option>
                    {TIPI_ENTE.map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </Select>
                </Field>
              </div>
              <Field
                label="Servizio"
                required
                hint={cartelleCreate ? "Cartella già creata: il nome della cartella non cambia" : "La sigla entra nel nome della cartella"}
              >
                <Select className={s.full} value={servizio} onChange={(_, d) => setServizio(d.value)}>
                  <option value="">Scegli…</option>
                  {opzioni.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.fields.Sigla} – {x.fields.Title}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Link all'avviso o al portale">
                <Input type="url" value={bando} placeholder="https://…" onChange={(_, d) => setBando(d.value)} />
              </Field>
            </Section>
          </div>
          <div className={s.col}>
            <Section title="Iscrizione">
              <div className={s.two}>
                <Field label="Data di iscrizione" hint="Vuota finché l'iscrizione non è ottenuta">
                  <Input type="date" value={iscrizione} onChange={(_, d) => setIscrizione(d.value)} />
                </Field>
                <Field label="Scadenza / rinnovo" hint="Segnalata 30 giorni prima">
                  <Input type="date" value={rinnovo} onChange={(_, d) => setRinnovo(d.value)} />
                </Field>
              </div>
              <Field label="Note">
                <Textarea rows={4} resize="vertical" value={note} onChange={(_, d) => setNote(d.value)} />
              </Field>
            </Section>
          </div>
        </div>
      ) : (
      <div className={s.cols}>
        <div className={s.col}>
          <Section title="Gara">
            <Field label="Oggetto" required hint={oggetto.trim().length > MAX_TESTO ? `${oggetto.trim().length} caratteri: nell'elenco SharePoint il Titolo ne mostra i primi ${MAX_TESTO}, il testo intero resta nella colonna "Oggetto completo" e nell'app.` : undefined}>
              <Textarea rows={2} resize="vertical" value={oggetto} onChange={(_, d) => setOggetto(d.value)} />
            </Field>
            <div className={s.two}>
              <Field label="Stazione appaltante" required>
                <Input value={stazione} maxLength={MAX_TESTO} placeholder="Es. Comune di Alatri" onChange={(_, d) => setStazione(d.value)} />
              </Field>
              <Field label="Tipo di ente" hint={!tipoEnte ? "Se resta vuoto, si ricava dalla posizione della cartella" : undefined}>
                <Select className={s.full} value={tipoEnte} onChange={(_, d) => setTipoEnte(d.value as TipoEnte | "")}>
                  <option value="">—</option>
                  {TIPI_ENTE.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className={s.two}>
              <Field
                label="Servizio"
                required
                hint={cartelleCreate ? "Cartelle già create: il nome delle cartelle non cambia" : "La sigla entra nel nome della cartella"}
              >
                <Select className={s.full} value={servizio} onChange={(_, d) => setServizio(d.value)}>
                  <option value="">Scegli…</option>
                  {opzioni.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.fields.Sigla} – {x.fields.Title}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Importo a base d'asta (€)">
                <Input inputMode="decimal" value={importo} placeholder="0,00" onChange={(_, d) => setImporto(d.value)} />
              </Field>
            </div>
            <div className={s.row}>
              <div>
                <div>Gara a lotti</div>
                <div className={s.hint}>Una cartella per ciascun lotto; il CIG si indica sui lotti</div>
              </div>
              <Switch checked={perLotti} disabled={cartelleCreate} onChange={(_, d) => setPerLotti(d.checked)} aria-label="Gara a lotti" />
            </div>
            {!perLotti && (
              <Field label="CIG">
                <Input value={cig} maxLength={40} onChange={(_, d) => setCig(d.value)} />
              </Field>
            )}
            <Field label="Link al bando">
              <Input type="url" value={bando} placeholder="https://…" onChange={(_, d) => setBando(d.value)} />
            </Field>
            <Field label="Altri componenti RTI" hint={`Separati da punto e virgola. Vuoto = ${SOGGETTO_PRINCIPALE} da sola`}>
              <Input value={rti} maxLength={MAX_TESTO} placeholder="es. Cooperativa Rossi; Consorzio Verdi" onChange={(_, d) => setRti(d.value)} />
            </Field>
          </Section>
        </div>
        <div className={s.col}>
          <Section title="Date">
            <div className={s.col}>
              <Field label="Scadenza presentazione">
                <Input type="datetime-local" value={scadenza} onChange={(_, d) => setScadenza(d.value)} />
              </Field>
              <Field label="Manifestazione di interesse entro">
                <Input type="datetime-local" value={interesse} onChange={(_, d) => setInteresse(d.value)} />
              </Field>
              <Field label="Apertura buste">
                <Input type="datetime-local" value={buste} onChange={(_, d) => setBuste(d.value)} />
              </Field>
            </div>
          </Section>

          <Section title="Valutazione">
            <ChipGroup ariaLabel="Valutazione" options={VALUTAZIONI} value={valutazione} onChange={setValutazione} />
            <Field label="Note di valutazione">
              <Textarea rows={3} resize="vertical" value={note} onChange={(_, d) => setNote(d.value)} />
            </Field>
            {g && perLotti && <span className={s.hint}>Gara a lotti: l'esito e l'importo aggiudicato si indicano su ciascun lotto, nella scheda della gara.</span>}
            {g && !perLotti && (
              <div className={s.two}>
                <Field label="Esito gara">
                  <Select className={s.full} value={esito} onChange={(_, d) => setEsito(d.value as Esito | "")}>
                    <option value="">Non ancora noto</option>
                    {ESITI.map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </Select>
                </Field>
                {esito === "Aggiudicata" && (
                  <Field label="Importo aggiudicato (€)">
                    <Input inputMode="decimal" value={aggiudicato} placeholder="0,00" onChange={(_, d) => setAggiudicato(d.value)} />
                  </Field>
                )}
              </div>
            )}
          </Section>
        </div>
      </div>
      )}
    </Shell>
  );
}
